import showErrMessage from '@/utils/showErrMessage.js';
import { useMemoizedFn } from 'ahooks';
import axios from 'axios';
import { createDirectUploadTask } from 'qiniu-js';
import { useState, useRef } from 'react';

function getFileExtension(file: File): string | null {
  const fileName = file.name;
  const lastDotIndex = fileName.lastIndexOf('.');

  if (lastDotIndex !== -1 && lastDotIndex < fileName.length - 1) {
    return fileName.substring(lastDotIndex + 1).toLowerCase();
  }

  return null; // 如果没有找到扩展名，返回 null
}

const useUpload = ({
  onSuccess,
  onFileLoad,
  keyPrefix,
}: {
  onSuccess: (payload: { url: string }) => void;
  onFileLoad: FileReader['onload'];
  keyPrefix: string;
}) => {
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const selectedFile = useRef<File | null>(null);
  const uploadTaskRef = useRef<ReturnType<
    typeof createDirectUploadTask
  > | null>(null);

  const handleUpload = useMemoizedFn(async (file: File) => {
    if (!file) return;

    try {
      // 创建直传任务
      const uploadTask = createDirectUploadTask(
        {
          type: 'file',
          data: file,
          key: `${keyPrefix}/${+new Date()}.${getFileExtension(file)}`,
        },
        {
          async tokenProvider() {
            try {
              const res = await axios.get(
                'https://1257170569-5npdofnhzt.ap-singapore.tencentscf.com/api/uploadToken'
              );

              return res.data.uploadToken;
            } catch (err) {
              showErrMessage(err);
              throw err;
            }
          },
        }
      );

      uploadTaskRef.current = uploadTask;
      // 设置进度回调函数
      uploadTask.onProgress((progress, context) => {
        setUploadProgress(progress.percent);
      });

      //   // 设置完成回调函数
      //   uploadTask.onComplete((result, context) => {
      // 	// 处理完成回调
      //   });

      //   // 设置错误回调函数
      //   uploadTask.onError((error, context) => {
      // 	// 处理错误回调
      //   });

      const res = await uploadTask.start();
      setUploadProgress(0);

      // @ts-ignore type
      const url = JSON.parse(res.result)?.key as string;

      await onSuccess({ url });

      // 如果你想取消上传，可以调用 observable.cancel();
    } catch (error) {
      showErrMessage(error);
      console.error('Failed to get upload token:', error);
    }
  });

  const handleFileChange = useMemoizedFn(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        // STEP: 取消当前上传
        uploadTaskRef.current?.cancel();

        selectedFile.current = event.target.files[0];

        // 预览图片
        const reader = new FileReader();
        reader.onloadend = onFileLoad;
        reader.readAsDataURL(selectedFile.current); // 将文件读取为 Data URL

        // STEP: 上传图片
        handleUpload(selectedFile.current);
      } else {
        // setPreviewImage(null); // 如果没有选择文件，则清除预览
      }
    }
  );

  const reset = useMemoizedFn(() => {
    selectedFile.current = null;
    setUploadProgress(0);
    uploadTaskRef.current?.cancel();
  });

  return {
    handleFileChange,
    uploadProgress,
    reset,
  };
};

export default useUpload;
